// A mock draft's grades (MOCK_DRAFT.md §3, §6.1): every team's from the
// recap's own grader, its manager's first, for a mock that has just ended and
// for a kept one. Hindsight, which the recap may speak freely; nothing here
// says what to do next time.

import type { ReactNode } from 'react';
import type { DraftSeat, DraftView, SavedMock } from '@fsp/domain';
import type { DraftRecap } from '@fsp/draft';
import { useDetail } from '../../data/detail';
import { positionLabel, TeamMark } from '../../kit';
import { Caption, Text } from '../../kit/ui';
import { pickLabel } from './research';

/** What the grades say, from wherever they came. */
export interface MockGradesData {
  headline: string;
  /**
   * Every team, best first, and for a bot of a mock that has just ended what
   * its draft style came to (AI_MANAGERS.md §4.7); a kept mock keeps the grades alone.
   */
  teams: { teamId: string; rank: number; grade: string; style?: string | null }[];
  /** The manager's own: their team, grade and line, the pick that went furthest past the board, and their positions. */
  mine: {
    teamId: string;
    grade: string;
    line: string;
    best: { round: number; slot: number; name: string } | null;
    positions: { position: string; count: number }[];
  } | null;
}

/** The grades of a mock that has just ended. */
export function gradesOfRecap(
  recap: DraftRecap,
  view: DraftView,
  myTeamId: string,
): MockGradesData {
  const mine = recap.teams.find((t) => t.teamId === myTeamId) ?? null;
  const best = mine?.bestPick ?? null;
  const bestName =
    best === null
      ? null
      : (view.board.find((c) => c.overall === best.overall)?.player?.name ?? null);
  return {
    headline: recap.headline,
    teams: recap.teams.map((t) => ({
      teamId: t.teamId,
      rank: t.rank,
      grade: t.grade,
      style: t.style,
    })),
    mine:
      mine === null
        ? null
        : {
            teamId: mine.teamId,
            grade: mine.grade,
            line: mine.line,
            best:
              best === null || bestName === null
                ? null
                : { round: best.round, slot: best.slot, name: bestName },
            positions: mine.positions,
          },
  };
}

/** The grades of a kept mock, its manager's positions counted from its board. */
export function gradesOfSaved(saved: SavedMock): MockGradesData {
  const counts = new Map<string, number>();
  for (const c of saved.board) {
    if (c.teamId !== saved.ownerTeamId || c.player === null) continue;
    const p = c.player.positions[0] ?? '';
    counts.set(p, (counts.get(p) ?? 0) + 1);
  }
  const grade = saved.grades.find((g) => g.teamId === saved.ownerTeamId);
  const best = saved.best === null ? null : saved.board.find((c) => c.overall === saved.best);
  return {
    headline: saved.headline,
    teams: [...saved.grades].sort((a, b) => a.rank - b.rank),
    mine:
      grade === undefined
        ? null
        : {
            teamId: saved.ownerTeamId,
            grade: grade.grade,
            line: saved.line,
            best:
              best?.player == null
                ? null
                : { round: best.round, slot: best.slot, name: best.player.name },
            positions: [...counts.entries()]
              .map(([position, count]) => ({ position, count }))
              .sort((a, b) => b.count - a.count || a.position.localeCompare(b.position)),
          },
  };
}

export function MockGrades({
  grades,
  seats,
  owner,
  footer,
}: {
  grades: MockGradesData;
  seats: readonly DraftSeat[];
  /** Whose draft it was: null for the reader's own. */
  owner: string | null;
  /** What sits under the manager's own grade: whether it is kept, and sharing it. */
  footer?: ReactNode;
}) {
  const detail = useDetail();
  const seatOf = new Map(seats.map((s) => [s.team.id, s]));
  const { mine } = grades;
  const n = seats.length;
  const theirs = owner === null ? 'Your' : `${owner}’s`;
  return (
    <div className="fsp-dr-grades">
      {mine !== null ? (
        <div className="fsp-dr-mygrade">
          <span className="fsp-dr-grade fsp-disp" aria-label={`${theirs} grade: ${mine.grade}`}>
            {mine.grade}
          </span>
          <div className="fsp-ui-grow">
            <div className="fsp-name">{theirs} draft</div>
            <Text size="sm" block>
              {mine.line}
            </Text>
            {detail !== 'simple' && mine.best !== null ? (
              <Text size="xs" tone="secondary" block>
                Most value past where the board had him: {mine.best.name} at{' '}
                {pickLabel(mine.best.round, mine.best.slot)}
              </Text>
            ) : null}
            {detail !== 'simple' ? (
              <Text size="xs" tone="secondary" block>
                {mine.positions
                  .map((p) => `${String(p.count)} ${positionLabel(p.position)}`)
                  .join(' · ')}
              </Text>
            ) : null}
            {footer}
          </div>
        </div>
      ) : null}
      <Caption>{grades.headline}</Caption>
      {grades.teams.map((t) => {
        const seat = seatOf.get(t.teamId);
        if (!seat) return null;
        const isOwners = t.teamId === mine?.teamId;
        return (
          <div
            key={t.teamId}
            className="fsp-row fsp-dr-graderow"
            data-mine={isOwners || undefined}
            data-plain={detail === 'simple' || undefined}
          >
            {detail !== 'simple' ? <span className="fsp-num fsp-dr-rk">{t.rank}</span> : null}
            <TeamMark team={seat.team} size={24} />
            <div className="fsp-ui-grow">
              <div className="fsp-name">
                {isOwners ? (owner === null ? 'You' : owner) : seat.team.name}
              </div>
              <div className="fsp-sub">
                {isOwners
                  ? `Pick ${String(seat.slot)} of ${String(n)}`
                  : (seat.ai?.name ?? 'A bot')}
              </div>
              {!isOwners && t.style ? (
                <div className="fsp-sub fsp-ui-wrap-text">{t.style}</div>
              ) : null}
            </div>
            <span className="fsp-dr-grade-sm fsp-disp">{t.grade}</span>
          </div>
        );
      })}
    </div>
  );
}
